import { PhotoLightbox } from '@/components/photovogue/photo-lightbox/component';
import { useEffect, useState } from 'react';
import { usePageModeration } from '@/hooks/use-page-moderation';

import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { Button } from '@repo/ui/components/ui/button';
import { usePhotos } from '@/state/use-photos';
import { usePhotoSelection, ViewMode } from '@/state/use-photo-selection';
import type {
  ModerationAction,
  Photo,
  PhotoPatch,
} from '@/services/photovogue';
import { ModerationToolbar } from '@/components/photovogue/moderation-toolbar/component';
import {
  PhotoGrid,
  type PhotoView,
} from '@/components/photovogue/photo-grid/component';
import { PhotoPagination } from '@/components/photovogue/photo-pagination/component';
import { PhotoDetail } from '@/components/photovogue/photo-detail/component';

export const ModerationQueue = () => {
  const {
    data,
    count,
    error,
    loadingSpinner,
    hasActiveFilters,
    init,
    moderatePhoto,
    updatePhoto,
    updatePagination,
  } = usePhotos();
  const { viewMode, selectedIds, selectId, unSelectId } = usePhotoSelection();

  // Saved photos may leave the filtered queue; restart at its first remaining page.
  const moderation = usePageModeration(() =>
    updatePagination((pagination) => ({ ...pagination, page: 0 })),
  );

  const [view, setView] = useState<PhotoView>('grid');
  const [detailPhoto, setDetailPhoto] = useState<Photo | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    init();
  }, [init]);

  const selectable = moderation.active || viewMode === ViewMode.SELECT;

  const toggleSelect = (id: string) => {
    if (moderation.active) {
      moderation.toggle(id);
      return;
    }
    if (selectedIds.includes(id)) unSelectId(id);
    else selectId(id);
  };

  const openDetail = (photo: Photo) => {
    setDetailPhoto(photo);
    setDetailOpen(true);
  };

  const onModerate = (id: string, action: ModerationAction) => {
    moderatePhoto(id, action);
    setDetailOpen(false);
  };

  const onUpdate = async (id: string, patch: PhotoPatch) => {
    const updated = await updatePhoto(id, patch);
    if (updated) setDetailPhoto(updated);
    return updated;
  };

  return (
    <section>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Image moderation
        </h1>
        <p className="text-sm text-cn-muted-foreground">
          Review submitted photos and set their editorial state.
        </p>
      </header>

      <ModerationToolbar
        view={view}
        onViewChange={setView}
        moderation={moderation}
      />

      {moderation.error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800"
        >
          {moderation.error}
        </p>
      )}

      {!moderation.active && loadingSpinner ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} className="aspect-square w-full rounded-lg" />
          ))}
        </div>
      ) : !moderation.active && error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-6 py-10 text-center"
        >
          <p className="text-sm font-medium">Unable to load photos.</p>
          <p className="mt-1 text-xs text-cn-muted-foreground">
            The current count is unavailable. Try the request again.
          </p>
          <Button className="mt-4" size="sm" onClick={() => init()}>
            Retry
          </Button>
        </div>
      ) : !moderation.active && count === 0 ? (
        <div className="rounded-lg border border-dashed bg-white py-16 text-center">
          <p className="text-sm font-medium">
            {hasActiveFilters()
              ? 'No photos match these filters'
              : 'No photos to moderate'}
          </p>
          <p className="mt-1 text-xs text-cn-muted-foreground">
            {hasActiveFilters()
              ? 'Try clearing or adjusting the filters.'
              : 'New submissions will appear here.'}
          </p>
        </div>
      ) : (
        <>
          <PhotoGrid
            photos={moderation.photos ?? data}
            view={view}
            selectable={selectable}
            selectedIds={
              moderation.active ? moderation.selectedIds : selectedIds
            }
            moderating={moderation.active}
            disabled={moderation.saving}
            onToggleSelect={toggleSelect}
            onOpen={openDetail}
            onPreview={setPreviewPhoto}
            ignoredIds={moderation.ignoredIds}
            onChoice={moderation.setChoice}
          />
          <PhotoPagination beforePageChange={moderation.discard} />
        </>
      )}

      <PhotoLightbox
        photo={previewPhoto}
        open={previewPhoto !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewPhoto(null);
        }}
      />
      <PhotoDetail
        photo={detailPhoto}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onModerate={onModerate}
        onUpdate={onUpdate}
      />
    </section>
  );
};

export default ModerationQueue;
